import React from "react";
import styled from "styled-components";
import ReleaseDetailsForm from "./ReleaseDetailsForm";
import PlatFormArtistCard from "./PlatFormArtistCard";
import { useNavigate } from "react-router";
import { PlatFormUserInfoContentProps } from "../types";
import { FormattedMessage } from "react-intl";

const PlatFormUserInfoContent: React.FC<PlatFormUserInfoContentProps> = ({
  link = "",
  releases,
  preview,
}) => {
  const navigate = useNavigate();



  const releaseData = releases?.releases?.data ?? {};
  const coverUrl = String(releaseData?.cover_url ?? preview ?? "").trim();
  const hasCoverUrl = coverUrl.length > 0 && coverUrl !== "undefined" && coverUrl !== "null";
  const [coverLoaded, setCoverLoaded] = React.useState(false);
  const prevCoverUrlRef = React.useRef<string>("");
  const loadedCoverUrlsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    // Only show spinner when we actually have a URL to load.
    // If URL is empty, don't keep a spinner running forever.
    if (!hasCoverUrl) {
      setCoverLoaded(true);
      return;
    }

    // Reset loader ONLY when URL changes.
    if (prevCoverUrlRef.current !== coverUrl) {
      prevCoverUrlRef.current = coverUrl;
      // If we've already loaded this URL in this session, don't show spinner again.
      setCoverLoaded(loadedCoverUrlsRef.current.has(coverUrl));
    }
  }, [coverUrl, hasCoverUrl]);
  const artists: {
    role: string; id: number; name: string
  }[] = releaseData.artists || [];
  const handleRedirect = () => {
    if (link) navigate(link);
  };

  const platforms = (releaseData.platforms as unknown as { name?: string }[]) || [];
  const countries = (releaseData.countries as unknown as { name?: string }[]) || [];
  const platformsText = platforms.length ? `${platforms.length} platforms` : "—";
  const countriesText = "The Whole World";
  const truncateText = (value: any, max = 20) => {
    const s = String(value ?? "");
    if (s.length <= max) return s;
    return `${s.slice(0, max)}...`;
  };
  const releaseTypeText = (() => {
    const raw = String(releaseData?.release_type ?? "").toUpperCase();
    if (raw === "EP") return "EP";
    if (raw === "SINGLE") return "Single";
    if (raw === "ALBUM") return "Album";
    return raw ? raw.replace(/_/g, " ") : "Release";
  })();


  return (
    <UserInfoContent>
      <TopBlock>
        <ImgContent>
          <CoverFrame>
            {hasCoverUrl && (
              <PreviewImage
                src={coverUrl}
                alt="Cover"
                $loaded={coverLoaded}
                onLoad={() => {
                  loadedCoverUrlsRef.current.add(coverUrl);
                  setCoverLoaded(true);
                }}
                onError={() => {
                  // Don't spin forever on error; show the empty state.
                  loadedCoverUrlsRef.current.add(coverUrl);
                  setCoverLoaded(true);
                }}
              />
            )}
            {!hasCoverUrl && (
              <CoverEmpty />
            )}
            {hasCoverUrl && !coverLoaded && (
              <CoverPlaceholder>
                <CoverSpinner aria-label="Loading cover" />
              </CoverPlaceholder>
            )}
          </CoverFrame>
          <EditButton onClick={(e) => { e.stopPropagation(); handleRedirect() }}>
            <FormattedMessage
              id="NewRelease.Platform.Edit"
              defaultMessage="Edit"
            />
          </EditButton>
        </ImgContent>
        <UserInfoBox>
          <UserInfo>
            <UserName>
              {truncateText(releaseData?.title, 20)}
            </UserName>
            <UserData>{truncateText(releaseData?.version, 20)}</UserData>
          </UserInfo>
          <ReleaseInfo>
            <ReleaseDetailWrapper>
              <ReleaseDetails>
                {releaseTypeText + ", " + releaseData?.tracks?.length} tracks
              </ReleaseDetails>
            </ReleaseDetailWrapper>
            <ReleaseDetailWrapperPlat>
              <ReleaseDetails>
                {platformsText}
              </ReleaseDetails>
              {platforms?.length ? (
                <TooltipBox>
                  {platforms.map(p => p.name).filter(Boolean).join(', ')}
                </TooltipBox>
              ) : null}
            </ReleaseDetailWrapperPlat>
            <ReleaseDetailWrapper>
              <ReleaseDetails>
                {countriesText}
              </ReleaseDetails>
            </ReleaseDetailWrapper>

            <ReleaseDetailWrapper>
              <ReleaseDetails>
                <FormattedMessage
                  id="ReleaseDetails.UserInfoContent.EstimatedRelease"
                  defaultMessage="Estimated release {date}"
                  values={{ date: releaseData?.release_date || releaseData?.created_at || "—" }}
                />
              </ReleaseDetails>
            </ReleaseDetailWrapper>
          </ReleaseInfo>
        </UserInfoBox>
      </TopBlock>
      <ArtistsBox>
        <ArtistsTitle>Artists</ArtistsTitle>
        <MusicType>
          {artists?.map((artist, index) => (
            <span key={index} title={artist.name}>
              {artist.name}
              {artists.length - 1 !== index ? ", " : ""}
            </span>
          ))}
        </MusicType>

        <ArtistsGrid>
          {(() => {
            const list = Array.isArray(artists) ? artists : [];
            const primaryCandidates = list.filter((a: any) =>
              a?.is_primary === true ||
              a?.is_primary === 1 ||
              a?.role === "MAIN" ||
              a?.status === true
            );
            const primaryId = primaryCandidates[0]?.id ?? list[0]?.id;

            return list.map((artist: any) => (
              <PlatFormArtistCard
                key={artist?.id}
                name={artist.name}
                isPrimary={primaryId != null ? String(artist?.id) === String(primaryId) : false}
                onTogglePrimary={() =>
                  console.log(`Toggled primary for artist ${artist.id}`)
                }
                onDelete={() => console.log(`Deleted artist ${artist.id}`)}
              />
            ));
          })()}
        </ArtistsGrid>
        <ReleaseDetailsForm releases={releases} />
      </ArtistsBox>
    </UserInfoContent>
  );
};

const UserInfoContent = styled.div`
  width: 100%;
  display: flex;
  gap: 50px;

  /* Mobile: cover on top, then details under it */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 18px;
  }
`;

const TopBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const CoverFrame = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  border-radius: 20px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    border-radius: 16px;
  }
`;

const PreviewImage = styled.img<{ $loaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${(p) => (p.$loaded ? 1 : 0)};
  transition: opacity 120ms ease;
  display: block;
`;

const CoverPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
`;

const CoverEmpty = styled.div`
  position: absolute;
  inset: 0;
  background: #ffffff;
`;

const CoverSpinner = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 3px solid rgba(0, 0, 0, 0.12);
  border-top-color: rgba(0, 0, 0, 0.55);
  display: inline-block;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const UserInfoBox = styled.div`
  margin-top: 13px;
  display: flex;
  flex-direction: column;

`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.p`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;
  color: #000000;
  margin: 0;
`;

const UserData = styled.p`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;
  color: #00000080;
  margin: 0;
`;

const ReleaseInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 63px;

  @media (max-width: 768px) {
    padding-top: 16px;
    gap: 1px;
  }
`;

const ReleaseDetailWrapper = styled.div`
  position: relative; 
`;

const ReleaseDetailWrapperPlat = styled.div`
  position: relative;
  display: inline-block;

  /* On hover, only show tooltip; do not change cursor or underline text */
  &:hover div {
      display: block;
}
`;
const ImgContent = styled.div`
  position: relative;
  &:hover button {
    display: block;
    cursor:pointer
  }
`;

const ReleaseDetails = styled.p`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
  margin-bottom: 0;
  cursor: default;
  border-bottom: 1px solid #fffff;
`;

const TooltipBox = styled.div`
  display: none;
  position: absolute;
  bottom: 20px;
  left: 0;
  width: fit-content;
  max-width: min(520px, calc(100vw - 40px));
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  background-color:#E2E5FF;
  border-radius: 15px;
  font-family: SF Pro Display;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: 0;
  padding: 17px 14px 15px 14px;

  @media (max-width: 768px) {
    width: fit-content;
    max-width: calc(100vw - 40px);
    left: 0;
  }
`;

const EditButton = styled.button`
  display:none;
  width: 51px;
  height: 24px;
  border-radius: 100px;
  border:none;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0;
  position: absolute;
  right:18px;
  bottom:18px;

  @media (max-width: 768px) {
    display: block;
  }
`;

const ArtistsBox = styled.div`
  margin-top: 1px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const ArtistsTitle = styled.span`
  font-family: SF Pro Display;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
`;

const MusicType = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
`;

const ArtistsGrid = styled.div`
  width: max-content;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px;
  margin-top: 10px;

  @media (max-width: 768px) {
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

export default PlatFormUserInfoContent;

